import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Calendar from './Calendar';
import { formatDateWithWeekdayIST, parseFromDatabase, getCurrentDateIST } from '../utils/timezone';
import { API_BASE_URL } from '../config/api';
import BulkIdCardPrint from './BulkIdCardPrint';
import BulkTeacherIdCardPrint from './BulkTeacherIdCardPrint';
import BulkAdmitCardPrint from './BulkAdmitCardPrint';
import TeacherDocuments from './TeacherDocuments';
import AIPaperGenerator from './AIPaperGenerator';
import PrincipalPaperMonitor from './AIPaper/PrincipalPaperMonitor';
import QuestionPaperBuilder from './QuestionPaperBuilder/QuestionPaperBuilder';
import PrincipalWhatsAppSettings from './AttendanceMaster/PrincipalWhatsAppSettings';
import AttendanceLauncher from './AttendanceMaster/AttendanceLauncher';
import AdminStreamControl from './LiveStream/AdminStreamControl';
import OnlineStaffTracker from './OnlineStaffTracker';
import PrincipalComplaintsPanel from './PrincipalComplaintsPanel';
import TeacherRecordView from './TeacherRecordView';
import BulkDataManager from './BulkDataManager';
import DiaryManagerPanel from './DiaryManagerPanel';
import ExecutiveSchoolHub from './ExecutiveSchoolHub';
import './PrincipalDashboard.css';

function PrincipalDashboard({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [teacherAttendanceData, setTeacherAttendanceData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showStudentData, setShowStudentData] = useState(false);
  const [selectedClass, setSelectedClass] = useState('all');
  const [studentsByClass, setStudentsByClass] = useState({});
  const [showBulkIdCard, setShowBulkIdCard] = useState(false);
  const [showTeacherIdCard, setShowTeacherIdCard] = useState(false);
  const [showAdmitCard, setShowAdmitCard] = useState(false);
  const [showTeacherDocs, setShowTeacherDocs] = useState(false);
  const [showAIPaper, setShowAIPaper] = useState(false);
  const [showPaperMonitor, setShowPaperMonitor] = useState(false);
  const [showPaperControl, setShowPaperControl] = useState(false);
  const [showSchoolHub, setShowSchoolHub] = useState(false);
  const [showWaSettings, setShowWaSettings] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [recordTeacher, setRecordTeacher] = useState(null);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [promoteForm, setPromoteForm] = useState({ studentId: '', fromClass: '', toClass: '', fromYear: '2025-2026', toYear: '2026-2027' });
  const [migrationResult, setMigrationResult] = useState(null);
  const [promoteMsg, setPromoteMsg] = useState('');
  const [currentAcademicYear, setCurrentAcademicYear] = useState('2026-2027');
  const [allAcademicYears, setAllAcademicYears] = useState([]);
  const [yearSaveMsg, setYearSaveMsg] = useState('');
  const [yearSaving, setYearSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchTeacherAttendance();
    fetchStudentsByClass();
    fetchAcademicYears();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const [currentRes, allRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/academic-year/current`),
        axios.get(`${API_BASE_URL}/api/academic-years`)
      ]);
      setCurrentAcademicYear(currentRes.data?.year || '2026-2027');
      const years = allRes.data || [];
      // Always ensure 2026-2027 and 2025-2026 are in the list
      const yearStrings = years.map(y => y.year);
      const defaults = ['2026-2027', '2025-2026', '2024-2025'];
      defaults.forEach(y => { if (!yearStrings.includes(y)) yearStrings.push(y); });
      setAllAcademicYears(yearStrings.sort().reverse());
    } catch {
      setAllAcademicYears(['2026-2027', '2025-2026', '2024-2025']);
    }
  };

  const saveAcademicYear = async (year) => {
    setYearSaving(true);
    setYearSaveMsg('');
    try {
      await axios.post(`${API_BASE_URL}/api/academic-year`, {
        year,
        startMonth: 'April',
        endMonth: 'March'
      });
      setCurrentAcademicYear(year);
      setYearSaveMsg(`✅ Current academic year set to ${year}`);
    } catch (err) {
      setYearSaveMsg('❌ Failed to save: ' + (err.response?.data?.error || err.message));
    }
    setYearSaving(false);
    setTimeout(() => setYearSaveMsg(''), 3000);
  };

  useEffect(() => {
    if (selectedDate) {
      fetchTeacherAttendance();
    }
  }, [selectedDate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchStudentsByClass = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users?role=student`);
      const students = response.data;
      const grouped = students.reduce((acc, student) => {
        const className = student.class || 'Unassigned';
        if (!acc[className]) acc[className] = [];
        acc[className].push(student);
        return acc;
      }, {});
      setStudentsByClass(grouped);
    } catch (error) {
      console.error('Error fetching students by class:', error);
    }
  };

  const showTermWiseMarks = async (studentId, studentName) => {
    try {
      console.log('Fetching marksheet for student:', studentId);
      const response = await axios.get(`${API_BASE_URL}/api/marksheet/${studentId}`);
      const marksheet = response.data;
      console.log('Marksheet data:', marksheet);
      
      let marksContent = '';
      const exams = marksheet.exams || {};
      const visibility = marksheet.visibility || {};
      
      Object.keys(exams).forEach(term => {
        const marks = exams[term];
        if (marks && Object.keys(marks).length > 0) {
          marksContent += `
            <div style="margin-bottom:20px;border:2px solid #e5e7eb;border-radius:10px;overflow:hidden">
              <h3 style="background:${visibility[term] ? '#d4edda' : '#f8d7da'};margin:0;padding:15px;color:#374151;text-transform:capitalize">
                ${term.replace(/([A-Z])/g, ' $1')} ${visibility[term] ? '✅' : '❌'}
              </h3>
              <div style="padding:15px">
                <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px">
                  ${['hindi', 'english', 'maths', 'evs', 'gk', 'computer'].map(subject => {
                    const mark = marks[subject];
                    return `
                      <div style="text-align:center;padding:10px;background:#f8fafc;border-radius:8px;border:1px solid #e5e7eb">
                        <div style="font-weight:bold;color:#1f2937;text-transform:capitalize">${subject}</div>
                        <div style="font-size:20px;font-weight:800;color:${mark >= 70 ? '#059669' : mark >= 50 ? '#d97706' : '#dc2626'}">${mark || '-'}</div>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            </div>
          `;
        }
      });
      
      if (!marksContent) {
        marksContent = '<div style="text-align:center;padding:40px;color:#6b7280">No exam data found for this student</div>';
      }
      
      const marksModal = document.createElement('div');
      marksModal.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;background:rgba(0,0,0,0.8);z-index:10000;display:flex;justify-content:center;align-items:flex-start;overflow-y:auto;padding:10px';
      marksModal.innerHTML = `
        <div style="background:white;width:100%;max-width:900px;border-radius:15px;overflow:hidden;box-shadow:0 10px 30px rgba(0,0,0,0.3)">
          <div style="background:#1e40af;color:white;padding:20px;text-align:center;position:relative">
            <h2 style="margin:0;font-size:24px">📊 ${studentName} - All Term Marks</h2>
            <button onclick="this.closest('div').parentElement.parentElement.remove()" style="position:absolute;top:15px;right:20px;background:none;border:none;color:white;font-size:24px;cursor:pointer">×</button>
          </div>
          <div style="padding:20px;max-height:70vh;overflow-y:auto">
            ${marksContent}
          </div>
        </div>
      `;
      
      marksModal.onclick = (e) => {
        if (e.target === marksModal) marksModal.remove();
      };
      
      document.body.appendChild(marksModal);
    } catch (error) {
      console.error('Error loading marksheet:', error);
      alert('Error loading term-wise marks: ' + (error.response?.data?.message || error.message));
    }
  };

  const viewClassMarksheets = async (className) => {
    try {
      console.log('Fetching marksheets for class:', className);
      const response = await axios.get(`${API_BASE_URL}/api/class-marksheets/${className}`);
      const marksheets = response.data;
      console.log('Marksheets received:', marksheets);
      
      if (!marksheets || marksheets.length === 0) {
        alert(`No marksheets found for class ${className}. Please ensure marksheets have been created for students in this class.`);
        return;
      }
      
      const modal = document.createElement('div');
      modal.id = 'marksheet-modal';
      modal.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;background:rgba(0,0,0,0.8);z-index:9999;display:flex;justify-content:center;align-items:flex-start;overflow-y:auto;padding:10px';
      modal.innerHTML = `
        <div style="background:white;width:100%;max-width:1200px;border-radius:15px;overflow:hidden;box-shadow:0 10px 30px rgba(0,0,0,0.3)">
          <div style="display:flex;justify-content:space-between;align-items:center;padding:20px;border-bottom:2px solid #ffc107;background:linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)">
            <h2 style="color:#856404;margin:0;font-size:24px;font-weight:700">📋 Class ${className} Marksheets (${marksheets.length} students)</h2>
            <button id="close-marksheet" style="background:#dc3545;color:white;border:none;padding:12px 16px;border-radius:8px;cursor:pointer;font-size:16px;font-weight:600;box-shadow:0 4px 15px rgba(220,53,69,0.4)">✕ Close</button>
          </div>
          <div style="padding:20px;max-height:70vh;overflow-y:auto">
            <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:15px">
              ${marksheets.map(marksheet => `
                <div style="background:linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);padding:15px;border-radius:10px;border:2px solid #ffc107;box-shadow:0 4px 15px rgba(255,193,7,0.2)">
                  <h4 style="color:#856404;margin:0 0 10px 0;font-size:16px;font-weight:600">${marksheet.name}</h4>
                  <p style="margin:5px 0;font-size:14px"><strong>Student ID:</strong> ${marksheet.studentId}</p>
                  <p style="margin:5px 0;font-size:14px"><strong>Father:</strong> ${marksheet.fathersName}</p>
                  <p style="margin:5px 0;font-size:14px"><strong>Class Teacher:</strong> ${marksheet.classTeacher}</p>
                  <div style="margin-top:10px">
                    <strong style="font-size:14px">Exam Visibility:</strong><br>
                    <div style="margin-top:5px">
                      ${Object.entries(marksheet.visibility || {
                        term1: false,
                        term2: false,
                        halfYearly: false,
                        term3: false,
                        project: false,
                        annualExam: false,
                        finalResult: false
                      }).map(([exam, visible]) => 
                        `<span style="display:inline-block;margin:2px 5px 2px 0;padding:2px 6px;border-radius:4px;font-size:12px;font-weight:600;background:${visible ? '#d4edda' : '#f8d7da'};color:${visible ? '#155724' : '#721c24'}">${exam}: ${visible ? '✅' : '❌'}</span>`
                      ).join('')}
                    </div>
                  </div>
                  <button onclick="window.showTermWiseMarks('${marksheet.studentId}', '${marksheet.name}')" style="background:#007bff;color:white;border:none;padding:10px 15px;border-radius:8px;cursor:pointer;margin-top:15px;width:100%;font-weight:600;box-shadow:0 4px 15px rgba(0,123,255,0.3)">📊 View Term Marks</button>
                </div>
              `).join('')}
              ${marksheets.length === 0 ? '<div style="text-align:center;color:#666;padding:60px;grid-column:1/-1;font-size:18px"><div style="font-size:48px;margin-bottom:16px">📋</div>No marksheets found for this class</div>' : ''}
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
      
      const closeBtn = document.getElementById('close-marksheet');
      const closeModal = () => {
        if (modal && modal.parentNode) {
          document.body.removeChild(modal);
        }
      };
      
      closeBtn.onclick = closeModal;
      modal.onclick = (e) => {
        if (e.target === modal) closeModal();
      };
      
      // Set global function reference
      window.showTermWiseMarks = showTermWiseMarks;
    } catch (error) {
      console.error('Error loading marksheets:', error);
      alert('Error loading marksheets: ' + (error.response?.data?.error || error.message));
    }
  };

  const viewClassAttendance = async (className) => {
    try {
      const currentDate = new Date();
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      const response = await axios.get(`${API_BASE_URL}/api/class-attendance/${className}?month=${month}&year=${year}`);
      const attendanceRecords = response.data;
      
      const modal = document.createElement('div');
      modal.id = 'attendance-modal';
      modal.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;background:rgba(0,0,0,0.8);z-index:9999;display:flex;justify-content:center;align-items:flex-start;overflow-y:auto;padding:10px';
      modal.innerHTML = `
        <div style="background:white;width:100%;max-width:1200px;border-radius:15px;overflow:hidden;box-shadow:0 10px 30px rgba(0,0,0,0.3)">
          <div style="display:flex;justify-content:space-between;align-items:center;padding:20px;border-bottom:2px solid #17a2b8;background:linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)">
            <h2 style="color:#0c5460;margin:0;font-size:24px;font-weight:700">📅 Class ${className} Attendance - ${currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h2>
            <button id="close-attendance" style="background:#dc3545;color:white;border:none;padding:12px 16px;border-radius:8px;cursor:pointer;font-size:16px;font-weight:600;box-shadow:0 4px 15px rgba(220,53,69,0.4)">✕ Close</button>
          </div>
          <div style="padding:20px;max-height:70vh;overflow-y:auto">
            <div style="margin-bottom:20px;padding:20px;background:linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);border-radius:12px;border:2px solid #17a2b8">
              <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:20px;text-align:center">
                <div>
                  <div style="font-size:32px;font-weight:bold;color:#28a745">${attendanceRecords.filter(r => r.status === 'present').length}</div>
                  <div style="color:#0c5460;font-weight:600;font-size:14px">Present Records</div>
                </div>
                <div>
                  <div style="font-size:32px;font-weight:bold;color:#dc3545">${attendanceRecords.filter(r => r.status === 'absent').length}</div>
                  <div style="color:#0c5460;font-weight:600;font-size:14px">Absent Records</div>
                </div>
                <div>
                  <div style="font-size:32px;font-weight:bold;color:#17a2b8">${attendanceRecords.length}</div>
                  <div style="color:#0c5460;font-weight:600;font-size:14px">Total Records</div>
                </div>
              </div>
            </div>
            ${attendanceRecords.length > 0 ? `
              <div style="background:white;border-radius:10px;overflow:hidden;box-shadow:0 4px 15px rgba(0,0,0,0.1)">
                <table style="width:100%;border-collapse:collapse">
                  <thead>
                    <tr style="background:linear-gradient(135deg, #17a2b8 0%, #138496 100%);color:white">
                      <th style="padding:15px;text-align:left;font-weight:600">Date</th>
                      <th style="padding:15px;text-align:left;font-weight:600">Student ID</th>
                      <th style="padding:15px;text-align:center;font-weight:600">Status</th>
                      <th style="padding:15px;text-align:left;font-weight:600">Marked By</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${attendanceRecords.map((record, index) => `
                      <tr style="border-bottom:1px solid #dee2e6;background:${index % 2 === 0 ? '#f8f9fa' : 'white'}">
                        <td style="padding:12px;font-weight:500">${record.date}</td>
                        <td style="padding:12px;font-weight:500">${record.studentId}</td>
                        <td style="padding:12px;text-align:center">
                          <span style="background:${record.status === 'present' ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' : 'linear-gradient(135deg, #dc3545 0%, #e74c3c 100)'};color:white;padding:6px 12px;border-radius:20px;font-size:12px;font-weight:600;box-shadow:0 2px 8px rgba(0,0,0,0.2)">
                            ${record.status === 'present' ? '✓ PRESENT' : '✗ ABSENT'}
                          </span>
                        </td>
                        <td style="padding:12px;font-weight:500;color:#6c757d">${record.markedBy}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            ` : `
              <div style="text-align:center;color:#666;padding:60px;font-size:18px">
                <div style="font-size:64px;margin-bottom:20px">📅</div>
                <h3 style="color:#6c757d;margin-bottom:10px">No Attendance Records Found</h3>
                <p style="color:#6c757d">No attendance records found for this class in ${currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
              </div>
            `}
          </div>
        </div>
      `;
      document.body.appendChild(modal);
      
      const closeBtn = document.getElementById('close-attendance');
      const closeModal = () => {
        if (modal && modal.parentNode) {
          document.body.removeChild(modal);
        }
      };
      
      closeBtn.onclick = closeModal;
      modal.onclick = (e) => {
        if (e.target === modal) closeModal();
      };
    } catch (error) {
      alert('Error loading attendance: ' + error.message);
    }
  };

  const fetchTeacherAttendance = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/teacher-attendance?date=${selectedDate}`);
      setTeacherAttendanceData(response.data || []);
    } catch (error) {
      console.error('Error fetching teacher attendance:', error);
      setTeacherAttendanceData([]);
    }
  };

  const editTeacherAttendance = async (teacherId, teacherName, status) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/teacher-attendance`, {
        teacherId,
        teacherName,
        date: selectedDate,
        status,
        markedBy: user.name,
        remarks: 'Edited by Principal'
      });
      await fetchTeacherAttendance();
      alert(`Teacher attendance updated to ${status}!`);
    } catch (error) {
      console.error('Error updating teacher attendance:', error);
      alert(`Error updating attendance: ${error.response?.data?.error || error.message}`);
    }
  };

  const getTeacherAttendanceStatus = (teacherId) => {
    if (!Array.isArray(teacherAttendanceData)) return null;
    const record = teacherAttendanceData.find(att => att.teacherId === teacherId && att.date === selectedDate);
    return record ? record.status : null;
  };

  const viewTeacherProfile = (teacher) => {
    setRecordTeacher(teacher);
  };

  // Legacy DOM-based profile modal (superseded by TeacherRecordView)
  // eslint-disable-next-line no-unused-vars
  const viewTeacherProfileLegacy = (teacher) => {
    const profileModal = document.createElement('div');
    profileModal.className = 'profile-modal';
    profileModal.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.8); z-index: 9999; display: flex; justify-content: center; align-items: flex-start; overflow-y: auto; -webkit-overflow-scrolling: touch; padding: 10px;">
        <div style="background: white; padding: 20px; width: 100%; max-width: 600px; border-radius: 15px; box-shadow: 0 10px 30px rgba(0,0,0,0.3);">
          <h3 style="margin-bottom: 20px; color: #333; text-align: center;">👨🏫 Teacher Profile</h3>
          <div style="text-align: center; margin-bottom: 20px;">
            <div style="width: 100px; height: 100px; margin: 0 auto 15px; border: 3px solid #007bff; background: ${teacher.profilePicture ? `url(${teacher.profilePicture})` : '#f0f0f0'}; backgroundSize: 'cover'; backgroundPosition: 'center'; display: flex; alignItems: 'center'; justifyContent: 'center'; fontSize: '40px'; borderRadius: '50%';">
              ${!teacher.profilePicture ? '👨🏫' : ''}
            </div>
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Name:</strong> ${teacher.name || 'N/A'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Email:</strong> ${teacher.email || 'N/A'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Assigned Class:</strong> ${teacher.class || 'Unassigned'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Section:</strong> ${teacher.section || 'No Section'}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Today's Attendance:</strong> <span style="color: ${getTeacherAttendanceStatus(teacher._id) === 'present' ? '#4caf50' : getTeacherAttendanceStatus(teacher._id) === 'absent' ? '#f44336' : getTeacherAttendanceStatus(teacher._id) === 'late' ? '#ff9800' : '#666'}; font-weight: bold;">${getTeacherAttendanceStatus(teacher._id) ? getTeacherAttendanceStatus(teacher._id).toUpperCase() : 'NOT MARKED'}</span>
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Role:</strong> Teacher
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Account Created:</strong> ${teacher.createdAt ? new Date(teacher.createdAt).toLocaleDateString() : 'N/A'}
          </div>
          <div style="text-align: center; margin-top: 20px;">
            <button id="closeProfile" onclick="this.closest('.profile-modal').remove()" ontouchstart="this.closest('.profile-modal').remove()" style="padding: 10px 20px; background: #dc3545; color: white; border: none; border-radius: 5px; cursor: pointer; font-weight: bold; touch-action: manipulation;">❌ Close</button>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(profileModal);
    
    const closeBtn = document.getElementById('closeProfile');
    closeBtn.onclick = () => {
      document.body.removeChild(profileModal);
    };
  };

  const getPresentTeachersCount = () => {
    if (!Array.isArray(teacherAttendanceData)) return 0;
    return teacherAttendanceData.filter(att => att.status === 'present' && att.date === selectedDate).length;
  };

  const allClasses = [...new Set(Object.keys(studentsByClass))].sort();
  const allStudents = Object.values(studentsByClass).flat();

  const handlePromoteStudent = async () => {
    if (!promoteForm.studentId || !promoteForm.toClass || !promoteForm.toYear) {
      setPromoteMsg('Please fill all fields.'); return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/promote-student`, { ...promoteForm, promotedBy: user.name });
      setPromoteMsg('Student promoted successfully!');
      fetchStudentsByClass();
      setTimeout(() => {
        setShowPromoteModal(false);
        setPromoteMsg('');
        setPromoteForm({ studentId: '', fromClass: '', toClass: '', fromYear: '2025-2026', toYear: '2026-2027' });
      }, 1500);
    } catch (err) {
      setPromoteMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRunMigration = async () => {
    setMigrationResult(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/academic-history/run-migration`);
      setMigrationResult(res.data);
    } catch (err) {
      setMigrationResult({ error: err.response?.data?.error || err.message });
    }
  };

  const rankTeacher = (teacher) => {
    const rankingModal = document.createElement('div');
    const parameters = [
      'Teaching Skills', 'Lesson Planning (Diary)', 'Content Knowledge', 'Classroom Engagement',
      'Use of Technology', 'Students understanding', 'Classroom Management', 'Discipline and Behaviour Management',
      'Time Management', 'Student Relations', 'Lunch Duties', 'Professionalism',
      'Punctuality and Attendance', 'Communication with Parents', 'Collaboration with Colleagues',
      'English and language communication', 'Dedication', 'Required Extra time',
      'Student Performance', 'Student Academic Progress', 'Student Learning and Copy Check',
      'Students Discipline', 'Personality development', 'Test Preparation'
    ];
    
    rankingModal.className = 'ranking-modal';
    rankingModal.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.8); z-index: 9999; display: flex; justify-content: center; align-items: flex-start; overflow-y: auto; -webkit-overflow-scrolling: touch; padding: 10px;">
        <div style="background: white; width: 100%; max-width: 800px; border-radius: 15px; box-shadow: 0 10px 30px rgba(0,0,0,0.3);">
          <div style="padding: 15px 20px; border-bottom: 2px solid #f59e0b; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-radius: 15px 15px 0 0;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div style="flex: 1;">
                <h2 style="margin: 0; color: #92400e; font-size: 20px; font-weight: bold;">⭐ Monthly Teacher Ranking</h2>
                <p style="margin: 5px 0 0 0; color: #92400e; font-size: 14px;">${teacher.name} - ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
              </div>
              <button id="closeRanking" onclick="this.closest('.ranking-modal').remove()" ontouchstart="this.closest('.ranking-modal').remove()" style="padding: 8px; background: #dc3545; color: white; border: none; border-radius: 50%; cursor: pointer; font-size: 16px; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; touch-action: manipulation;">✕</button>
            </div>
          </div>
          
          <div style="padding: 20px; max-height: 70vh; overflow-y: auto;">
            <form id="rankingForm">
              <div style="display: grid; grid-template-columns: 1fr; gap: 15px;">
                ${parameters.map(param => `
                  <div style="padding: 15px; background: #f8f9fa; border-radius: 10px; border: 1px solid #dee2e6;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                      <label style="font-weight: bold; color: #495057; font-size: 14px;">${param}:</label>
                      <span id="${param.replace(/[^a-zA-Z0-9]/g, '')}_value" style="font-weight: bold; color: #007bff; font-size: 16px;">5</span>
                    </div>
                    <input type="range" name="${param.replace(/[^a-zA-Z0-9]/g, '')}" min="1" max="10" value="5" 
                           style="width: 100%; height: 8px; border-radius: 5px; background: #ddd; outline: none;"
                           oninput="document.getElementById('${param.replace(/[^a-zA-Z0-9]/g, '')}_value').textContent = this.value">
                    <div style="display: flex; justify-content: space-between; font-size: 12px; color: #666; margin-top: 5px;">
                      <span>Poor (1)</span>
                      <span>Average (5)</span>
                      <span>Excellent (10)</span>
                    </div>
                  </div>
                `).join('')}
              </div>
              
              <div style="margin-top: 20px; padding: 15px; background: #e3f2fd; border-radius: 10px;">
                <label style="display: block; margin-bottom: 10px; font-weight: bold; color: #1565c0;">📝 Additional Comments:</label>
                <textarea name="comments" rows="3" placeholder="Optional feedback and suggestions for improvement..."
                         style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; resize: vertical;"></textarea>
              </div>
              
              <div style="text-align: center; margin-top: 20px;">
                <button type="submit" style="padding: 12px 30px; background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 16px; font-weight: bold; box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);">⭐ Save Monthly Ranking</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(rankingModal);
    
    const closeBtn = document.getElementById('closeRanking');
    const form = document.getElementById('rankingForm');
    
    const closeModal = () => {
      if (rankingModal && rankingModal.parentNode) {
        document.body.removeChild(rankingModal);
      }
    };
    
    closeBtn.onclick = closeModal;
    closeBtn.ontouchstart = closeModal;
    
    form.onsubmit = async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const ranking = {};
      let totalScore = 0;
      
      parameters.forEach(param => {
        const key = param.replace(/[^a-zA-Z0-9]/g, '');
        const value = parseInt(formData.get(key));
        ranking[param] = value;
        totalScore += value;
      });
      
      const averageScore = (totalScore / parameters.length).toFixed(1);
      const comments = formData.get('comments');
      
      try {
        await axios.post(`${API_BASE_URL}/api/teacher-ranking`, {
          teacherId: teacher._id,
          teacherName: teacher.name,
          month: new Date().getMonth() + 1,
          year: new Date().getFullYear(),
          ranking: ranking,
          averageScore: parseFloat(averageScore),
          comments: comments,
          rankedBy: user.name
        });
        
        alert(`Monthly ranking saved successfully!\nAverage Score: ${averageScore}/10`);
        if (rankingModal && rankingModal.parentNode) {
          document.body.removeChild(rankingModal);
        }
      } catch (error) {
        console.error('Error saving ranking:', error);
        alert('Error saving ranking: ' + (error.response?.data?.message || error.message));
      }
    };
  };

  const impersonateTeacher = async (teacher) => {
    try {
      // Store current principal session with all necessary data
      const principalSession = {
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          role: 'principal',
          _id: user._id
        },
        timestamp: Date.now()
      };
      sessionStorage.setItem('principalSession', JSON.stringify(principalSession));
      
      // Create teacher session with proper structure
      const teacherUser = {
        id: teacher._id,
        _id: teacher._id,
        name: teacher.name,
        email: teacher.email,
        role: 'teacher',
        class: teacher.class,
        section: teacher.section,
        impersonatedBy: user.name,
        originalRole: 'principal'
      };
      
      // Store teacher session and impersonation flag
      localStorage.setItem('user', JSON.stringify(teacherUser));
      localStorage.setItem('isImpersonating', 'true');
      localStorage.setItem('originalPrincipal', JSON.stringify(principalSession.user));
      
      window.location.reload();
    } catch (error) {
      console.error('Error impersonating teacher:', error);
    }
  };

  const impersonateTuitionTeacher = async (tuitionTeacher) => {
    try {
      // Store current principal session with all necessary data
      const principalSession = {
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          role: 'principal',
          _id: user._id
        },
        timestamp: Date.now()
      };
      sessionStorage.setItem('principalSession', JSON.stringify(principalSession));
      
      // Create tuition teacher session with proper structure
      const tuitionTeacherUser = {
        id: tuitionTeacher._id,
        _id: tuitionTeacher._id,
        name: tuitionTeacher.name,
        email: tuitionTeacher.email,
        role: 'tuition_teacher',
        impersonatedBy: user.name,
        originalRole: 'principal'
      };
      
      // Store tuition teacher session and impersonation flag
      localStorage.setItem('user', JSON.stringify(tuitionTeacherUser));
      localStorage.setItem('isImpersonating', 'true');
      localStorage.setItem('originalPrincipal', JSON.stringify(principalSession.user));
      
      window.location.reload();
    } catch (error) {
      console.error('Error impersonating tuition teacher:', error);
    }
  };

  const viewTeacherDashboard = (teacher) => {
    const dashboardModal = document.createElement('div');
    dashboardModal.className = 'dashboard-modal';
    dashboardModal.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.8); z-index: 9999; display: flex; justify-content: center; align-items: flex-start; overflow-y: auto; -webkit-overflow-scrolling: touch; padding: 10px;">
        <div style="background: white; width: 100%; max-width: 1000px; border-radius: 15px; box-shadow: 0 10px 30px rgba(0,0,0,0.3);">
          <div style="padding: 20px; border-bottom: 2px solid #007bff; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-radius: 15px 15px 0 0;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div>
                <h2 style="margin: 0; color: #007bff; font-size: 24px; font-weight: bold;">👨🏫 ${teacher.name}'s Complete Dashboard</h2>
                <p style="margin: 5px 0 0 0; color: #666; font-size: 16px;">Full Teacher Profile & Management System</p>
              </div>
              <button id="closeDashboard" onclick="this.closest('.dashboard-modal').remove()" ontouchstart="this.closest('.dashboard-modal').remove()" style="padding: 10px 15px; background: #dc3545; color: white; border: none; border-radius: 50%; cursor: pointer; font-size: 18px; width: 45px; height: 45px; display: flex; align-items: center; justify-content: center; touch-action: manipulation;">❌</button>
            </div>
          </div>
          
          <div style="padding: 20px; max-height: 70vh; overflow-y: auto;">
            <!-- Teacher Profile Header -->
            <div style="display: grid; grid-template-columns: auto 1fr auto; gap: 20px; margin-bottom: 30px; padding: 20px; background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%); border-radius: 15px; border: 2px solid #2196f3;">
              <div style="display: flex; align-items: center; gap: 15px;">
                <div style="width: 80px; height: 80px; border: 3px solid #007bff; background: ${teacher.profilePicture ? `url(${teacher.profilePicture})` : '#f0f0f0'}; background-size: cover; background-position: center; display: flex; align-items: center; justify-content: center; font-size: 30px; border-radius: 50%;">
                  ${!teacher.profilePicture ? '👨🏫' : ''}
                </div>
                <div>
                  <h3 style="margin: 0; color: #1565c0; font-size: 20px;">${teacher.name}</h3>
                  <p style="margin: 2px 0; color: #1976d2;">Teacher - Class ${teacher.class || 'Unassigned'}</p>
                  <p style="margin: 2px 0; font-size: 14px; color: #666;">${teacher.email}</p>
                </div>
              </div>
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; align-content: center;">
                <div style="text-align: center; padding: 10px; background: rgba(255,255,255,0.7); border-radius: 8px;">
                  <div style="font-size: 20px; font-weight: bold; color: #1565c0;">${users.filter(u => u.role === 'student' && u.class === teacher.class).length}</div>
                  <div style="font-size: 12px; color: #666;">Students</div>
                </div>
                <div style="text-align: center; padding: 10px; background: rgba(255,255,255,0.7); border-radius: 8px;">
                  <div style="font-size: 20px; font-weight: bold; color: ${getTeacherAttendanceStatus(teacher._id) === 'present' ? '#4caf50' : getTeacherAttendanceStatus(teacher._id) === 'absent' ? '#f44336' : getTeacherAttendanceStatus(teacher._id) === 'late' ? '#ff9800' : '#666'};">${getTeacherAttendanceStatus(teacher._id) ? getTeacherAttendanceStatus(teacher._id).toUpperCase() : 'N/A'}</div>
                  <div style="font-size: 12px; color: #666;">Today's Status</div>
                </div>
              </div>
              <div style="display: flex; flex-direction: column; gap: 8px; align-items: flex-end;">
                <button onclick="alert('Direct messaging feature coming soon!')" style="padding: 8px 16px; background: #28a745; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 12px; font-weight: bold;">💬 Message</button>
                <button onclick="alert('Performance report generated!')" style="padding: 8px 16px; background: #17a2b8; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 12px; font-weight: bold;">📈 Reports</button>
              </div>
            </div>

            <!-- Quick Stats Dashboard -->
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 30px;">
              <div style="padding: 15px; background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%); border-radius: 10px; text-align: center; border: 2px solid #4caf50;">
                <div style="font-size: 24px; margin-bottom: 5px;">📚</div>
                <div style="font-size: 18px; font-weight: bold; color: #2e7d32;">Content</div>
                <div style="font-size: 14px; color: #388e3c;">Assignments & Materials</div>
              </div>
              <div style="padding: 15px; background: linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%); border-radius: 10px; text-align: center; border: 2px solid #ff9800;">
                <div style="font-size: 24px; margin-bottom: 5px;">📅</div>
                <div style="font-size: 18px; font-weight: bold; color: #e65100;">Attendance</div>
                <div style="font-size: 14px; color: #f57c00;">Student Management</div>
              </div>
              <div style="padding: 15px; background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%); border-radius: 10px; text-align: center; border: 2px solid #9c27b0;">
                <div style="font-size: 24px; margin-bottom: 5px;">📊</div>
                <div style="font-size: 18px; font-weight: bold; color: #6a1b9a;">Grades</div>
                <div style="font-size: 14px; color: #8e24aa;">Marksheets & Results</div>
              </div>
              <div style="padding: 15px; background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%); border-radius: 10px; text-align: center; border: 2px solid #2196f3;">
                <div style="font-size: 24px; margin-bottom: 5px;">📢</div>
                <div style="font-size: 18px; font-weight: bold; color: #1565c0;">Communication</div>
                <div style="font-size: 14px; color: #1976d2;">Announcements & Messages</div>
              </div>
            </div>

            <!-- Students Under This Teacher -->
            <div style="margin-bottom: 30px; padding: 20px; background: #f8f9fa; border-radius: 15px; border: 2px solid #dee2e6;">
              <h3 style="margin: 0 0 15px 0; color: #495057;">👥 Students in ${teacher.class || 'Unassigned Class'}</h3>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 15px; max-height: 300px; overflow-y: auto;">
                ${users.filter(u => u.role === 'student' && u.class === teacher.class).map(student => `
                  <div style="padding: 12px; background: white; border-radius: 8px; border: 1px solid #ddd; display: flex; align-items: center; gap: 10px;">
                    <div style="width: 40px; height: 40px; border: 2px solid #007bff; background: ${student.profilePicture ? `url(${student.profilePicture})` : '#f0f0f0'}; background-size: cover; background-position: center; display: flex; align-items: center; justify-content: center; font-size: 16px; border-radius: 50%;">
                      ${!student.profilePicture ? '👨🎓' : ''}
                    </div>
                    <div style="flex: 1;">
                      <div style="font-weight: bold; font-size: 14px;">${student.name}</div>
                      <div style="font-size: 12px; color: #666;">${student.email}</div>
                      <div style="font-size: 11px; color: #888;">ID: ${student.studentId || student._id?.slice(-6)}</div>
                    </div>
                    <div style="text-align: center;">
                      <div style="font-size: 10px; padding: 2px 6px; background: #e3f2fd; border-radius: 3px; color: #1565c0; font-weight: bold;">Active</div>
                    </div>
                  </div>
                `).join('')}
                ${users.filter(u => u.role === 'student' && u.class === teacher.class).length === 0 ? '<div style="text-align: center; color: #666; padding: 40px;">No students assigned to this class yet</div>' : ''}
              </div>
            </div>

            <!-- Teacher's Recent Activities -->
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px;">
              <div style="padding: 20px; background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%); border-radius: 15px; border: 2px solid #4caf50;">
                <h4 style="margin: 0 0 15px 0; color: #2e7d32;">📝 Recent Content Uploads</h4>
                <div style="max-height: 200px; overflow-y: auto;">
                  <div style="padding: 8px; background: rgba(255,255,255,0.7); border-radius: 6px; margin-bottom: 8px;">
                    <div style="font-weight: bold; font-size: 13px;">Math Assignment - Chapter 5</div>
                    <div style="font-size: 11px; color: #666;">Uploaded 2 days ago</div>
                  </div>
                  <div style="padding: 8px; background: rgba(255,255,255,0.7); border-radius: 6px; margin-bottom: 8px;">
                    <div style="font-weight: bold; font-size: 13px;">Science Notes - Photosynthesis</div>
                    <div style="font-size: 11px; color: #666;">Uploaded 1 week ago</div>
                  </div>
                  <div style="padding: 8px; background: rgba(255,255,255,0.7); border-radius: 6px;">
                    <div style="font-weight: bold; font-size: 13px;">English Grammar Worksheet</div>
                    <div style="font-size: 11px; color: #666;">Uploaded 1 week ago</div>
                  </div>
                </div>
              </div>
              
              <div style="padding: 20px; background: linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%); border-radius: 15px; border: 2px solid #ff9800;">
                <h4 style="margin: 0 0 15px 0; color: #e65100;">📅 Attendance Summary</h4>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;">
                  <div style="text-align: center; padding: 10px; background: rgba(255,255,255,0.7); border-radius: 6px;">
                    <div style="font-size: 18px; font-weight: bold; color: #4caf50;">92%</div>
                    <div style="font-size: 12px;">Class Average</div>
                  </div>
                  <div style="text-align: center; padding: 10px; background: rgba(255,255,255,0.7); border-radius: 6px;">
                    <div style="font-size: 18px; font-weight: bold; color: #2196f3;">${users.filter(u => u.role === 'student' && u.class === teacher.class).length}</div>
                    <div style="font-size: 12px;">Total Students</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 20px;">
              <button id="manageContent" style="padding: 15px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; border-radius: 10px; cursor: pointer; font-weight: bold; font-size: 14px;">
                📚 Manage Content
              </button>
              <button id="viewStudents" style="padding: 15px; background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); color: white; border: none; border-radius: 10px; cursor: pointer; font-weight: bold; font-size: 14px;">
                👥 View Students
              </button>
              <button id="manageGrades" style="padding: 15px; background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); color: white; border: none; border-radius: 10px; cursor: pointer; font-weight: bold; font-size: 14px;">
                📈 Manage Grades
              </button>
              <button id="communications" style="padding: 15px; background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); color: white; border: none; border-radius: 10px; cursor: pointer; font-weight: bold; font-size: 14px;">
                📢 Communications
              </button>
            </div>

            <!-- Content Sections (Initially Hidden) -->
            <div id="contentSection" style="display: none; padding: 20px; background: #f8f9fa; border-radius: 15px; margin-bottom: 20px;">
              <h4 style="color: #667eea; margin-bottom: 15px;">📚 Content Management</h4>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 15px;">
                <div style="padding: 15px; background: white; border-radius: 10px; border: 1px solid #ddd;">
                  <h5>Recent Uploads</h5>
                  <div style="font-size: 14px; color: #666;">
                    <p>• Math Assignment - Chapter 5 (2 days ago)</p>
                    <p>• Science Notes - Photosynthesis (1 week ago)</p>
                    <p>• English Grammar Worksheet (1 week ago)</p>
                  </div>
                </div>
                <div style="padding: 15px; background: white; border-radius: 10px; border: 1px solid #ddd;">
                  <h5>Quick Actions</h5>
                  <button onclick="alert('Upload new content')" style="padding: 8px 16px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer; margin: 5px;">Upload File</button>
                  <button onclick="alert('Create assignment')" style="padding: 8px 16px; background: #28a745; color: white; border: none; border-radius: 5px; cursor: pointer; margin: 5px;">New Assignment</button>
                </div>
              </div>
            </div>

            <div id="studentsSection" style="display: none; padding: 20px; background: #f8f9fa; border-radius: 15px; margin-bottom: 20px;">
              <h4 style="color: #f5576c; margin-bottom: 15px;">👥 Student Management</h4>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 15px;">
                ${users.filter(u => u.role === 'student' && u.class === teacher.class).map(student => `
                  <div style="padding: 15px; background: white; border-radius: 10px; border: 1px solid #ddd;">
                    <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
                      <div style="width: 40px; height: 40px; border: 2px solid #007bff; background: ${student.profilePicture ? `url(${student.profilePicture})` : '#f0f0f0'}; background-size: cover; background-position: center; display: flex; align-items: center; justify-content: center; font-size: 16px; border-radius: 50%;">
                        ${!student.profilePicture ? '👨🎓' : ''}
                      </div>
                      <div>
                        <div style="font-weight: bold;">${student.name}</div>
                        <div style="font-size: 12px; color: #666;">${student.email}</div>
                      </div>
                    </div>
                    <div style="font-size: 12px; color: #888;">
                      <p>ID: ${student.studentId || student._id?.slice(-6)}</p>
                      <p>Father: ${student.fathersName || 'N/A'}</p>
                      <p>Attendance: 92% | Grade: B+</p>
                    </div>
                    <button onclick="alert('Opening ${student.name} profile')" style="padding: 6px 12px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; width: 100%;">View Profile</button>
                  </div>
                `).join('')}
              </div>
            </div>

            <div id="gradesSection" style="display: none; padding: 20px; background: #f8f9fa; border-radius: 15px; margin-bottom: 20px;">
              <h4 style="color: #00f2fe; margin-bottom: 15px;">📈 Grade Management</h4>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 15px;">
                <div style="padding: 15px; background: white; border-radius: 10px; border: 1px solid #ddd;">
                  <h5>Recent Marksheets</h5>
                  <div style="font-size: 14px; color: #666;">
                    <p>• Term 1 Results - Completed (25 students)</p>
                    <p>• Half Yearly - In Progress (15/25)</p>
                    <p>• Project Marks - Pending</p>
                  </div>
                </div>
                <div style="padding: 15px; background: white; border-radius: 10px; border: 1px solid #ddd;">
                  <h5>Class Performance</h5>
                  <div style="text-align: center;">
                    <div style="font-size: 24px; font-weight: bold; color: #28a745;">B+</div>
                    <div style="font-size: 14px; color: #666;">Class Average</div>
                    <button onclick="alert('Opening grade entry')" style="padding: 8px 16px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer; margin-top: 10px;">Enter Grades</button>
                  </div>
                </div>
              </div>
            </div>

            <div id="communicationsSection" style="display: none; padding: 20px; background: #f8f9fa; border-radius: 15px; margin-bottom: 20px;">
              <h4 style="color: #38f9d7; margin-bottom: 15px;">📢 Communications</h4>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 15px;">
                <div style="padding: 15px; background: white; border-radius: 10px; border: 1px solid #ddd;">
                  <h5>Recent Announcements</h5>
                  <div style="font-size: 14px; color: #666;">
                    <p>• Math test on Friday (2 days ago)</p>
                    <p>• Science project due next week (5 days ago)</p>
                    <p>• Parent meeting reminder (1 week ago)</p>
                  </div>
                </div>
                <div style="padding: 15px; background: white; border-radius: 10px; border: 1px solid #ddd;">
                  <h5>Quick Communication</h5>
                  <button onclick="alert('Send class announcement')" style="padding: 8px 16px; background: #28a745; color: white; border: none; border-radius: 5px; cursor: pointer; margin: 5px; width: 100%;">Class Announcement</button>
                  <button onclick="alert('Message parents')" style="padding: 8px 16px; background: #17a2b8; color: white; border: none; border-radius: 5px; cursor: pointer; margin: 5px; width: 100%;">Message Parents</button>
                  <button onclick="alert('Send homework')" style="padding: 8px 16px; background: #ffc107; color: #212529; border: none; border-radius: 5px; cursor: pointer; margin: 5px; width: 100%;">Send Homework</button>
                </div>
              </div>
            </div>

            <!-- Performance Metrics -->
            <div style="padding: 20px; background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%); border-radius: 15px; border: 2px solid #9c27b0;">
              <h4 style="margin: 0 0 15px 0; color: #6a1b9a;">📈 Teacher Performance Metrics</h4>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 15px;">
                <div style="text-align: center; padding: 12px; background: rgba(255,255,255,0.7); border-radius: 8px;">
                  <div style="font-size: 20px; font-weight: bold; color: #6a1b9a;">95%</div>
                  <div style="font-size: 12px; color: #666;">Attendance Rate</div>
                </div>
                <div style="text-align: center; padding: 12px; background: rgba(255,255,255,0.7); border-radius: 8px;">
                  <div style="font-size: 20px; font-weight: bold; color: #6a1b9a;">87%</div>
                  <div style="font-size: 12px; color: #666;">Assignment Completion</div>
                </div>
                <div style="text-align: center; padding: 12px; background: rgba(255,255,255,0.7); border-radius: 8px;">
                  <div style="font-size: 20px; font-weight: bold; color: #6a1b9a;">4.2/5</div>
                  <div style="font-size: 12px; color: #666;">Parent Rating</div>
                </div>
                <div style="text-align: center; padding: 12px; background: rgba(255,255,255,0.7); border-radius: 8px;">
                  <div style="font-size: 20px; font-weight: bold; color: #6a1b9a;">B+</div>
                  <div style="font-size: 12px; color: #666;">Class Average</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(dashboardModal);
    
    const closeBtn = document.getElementById('closeDashboard');
    closeBtn.onclick = () => {
      if (dashboardModal && dashboardModal.parentNode) {
        document.body.removeChild(dashboardModal);
      }
    };

    // Add event listeners for action buttons
    const manageContentBtn = document.getElementById('manageContent');
    const viewStudentsBtn = document.getElementById('viewStudents');
    const manageGradesBtn = document.getElementById('manageGrades');
    const communicationsBtn = document.getElementById('communications');

    const contentSection = document.getElementById('contentSection');
    const studentsSection = document.getElementById('studentsSection');
    const gradesSection = document.getElementById('gradesSection');
    const communicationsSection = document.getElementById('communicationsSection');

    // Function to hide all sections
    const hideAllSections = () => {
      contentSection.style.display = 'none';
      studentsSection.style.display = 'none';
      gradesSection.style.display = 'none';
      communicationsSection.style.display = 'none';
    };

    manageContentBtn.onclick = () => {
      hideAllSections();
      contentSection.style.display = 'block';
    };

    viewStudentsBtn.onclick = () => {
      hideAllSections();
      studentsSection.style.display = 'block';
    };

    manageGradesBtn.onclick = () => {
      hideAllSections();
      gradesSection.style.display = 'block';
    };

    communicationsBtn.onclick = () => {
      hideAllSections();
      communicationsSection.style.display = 'block';
    };
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const todayLabel = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const staffList = users.filter(u => u.role === 'teacher' || u.role === 'tuition_teacher');
  const kpis = [
    { icon: '👨‍🎓', label: 'Total Students', value: users.filter(u => u.role === 'student').length, bg: '#dbeafe' },
    { icon: '👩‍🏫', label: 'Teaching Staff', value: staffList.length, bg: '#dcfce7' },
    { icon: '✅', label: 'Teachers Present', value: `${getPresentTeachersCount()}/${staffList.length}`, bg: '#fef9c3' },
    { icon: '📅', label: 'Academic Year', value: currentAcademicYear, bg: '#ede9fe' }
  ];

  return (
    <>
    <div className="dashboard admin-theme pd-root">
      <div className="pd-header">
        <div className="pd-header-left">
          <div className="pd-avatar">🎓</div>
          <div>
            <p className="pd-greet">{greeting},</p>
            <h1 className="pd-name">{user.name}</h1>
            <p className="pd-role">Principal · The Rising School</p>
          </div>
        </div>
        <div className="pd-header-right">
          <div className="pd-chip">📅 Academic Year {currentAcademicYear}</div>
          <div className="pd-chip">{todayLabel}</div>
          <button className="pd-logout" onClick={onLogout}>Logout</button>
        </div>
      </div>

      <div className="pd-calendar-wrap">
        <Calendar userRole="principal" userClass="all" userName={user.name} />
      </div>

      <div className="pd-body">
      <div className="pd-kpis">
        {kpis.map(k => (
          <div className="pd-kpi" key={k.label}>
            <div className="pd-kpi-icon" style={{ background: k.bg }}>{k.icon}</div>
            <div>
              <div className="pd-kpi-value">{k.value}</div>
              <div className="pd-kpi-label">{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid" style={{
        gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))'
      }}>

        <div className="pd-section-title"><h2>🛠️ Tools &amp; Documents</h2><span>Academic year, ID cards, admit cards, letters and AI paper generator</span></div>

        {/* Academic Year Management */}
        <div className="card" style={{ padding: window.innerWidth <= 768 ? '15px' : '20px', background: 'linear-gradient(135deg, #fdf4ff 0%, #ede9fe 100%)', border: '2px solid #7c3aed' }}>
          <h3 style={{ fontSize: window.innerWidth <= 768 ? '18px' : '20px', marginBottom: '8px', color: '#5b21b6' }}>📅 Academic Year Management</h3>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '14px' }}>Set the current academic year. Old records keep their original year permanently.</p>

          <div style={{ marginBottom: '12px', padding: '10px 14px', background: 'rgba(124,58,237,0.08)', borderRadius: '8px', border: '1px solid #c4b5fd' }}>
            <div style={{ fontSize: '12px', color: '#7c3aed', fontWeight: 600, marginBottom: '2px' }}>CURRENT ACADEMIC YEAR</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#4c1d95' }}>{currentAcademicYear}</div>
          </div>

          <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>Change Academic Year:</label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            <select
              defaultValue={currentAcademicYear}
              id="academicYearSelect"
              style={{ flex: 1, padding: '9px 10px', borderRadius: '8px', border: '1.5px solid #7c3aed', fontSize: '14px', fontWeight: 600 }}
            >
              {allAcademicYears.map(y => (
                <option key={y} value={y}>{y}{y === currentAcademicYear ? ' ✓ Current' : ''}</option>
              ))}
            </select>
            <button
              onClick={() => {
                const sel = document.getElementById('academicYearSelect').value;
                if (sel !== currentAcademicYear) saveAcademicYear(sel);
              }}
              disabled={yearSaving}
              style={{ padding: '9px 16px', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}
            >
              {yearSaving ? '...' : 'Set'}
            </button>
          </div>

          {yearSaveMsg && <div style={{ fontSize: '13px', padding: '6px 10px', borderRadius: '6px', background: yearSaveMsg.startsWith('✅') ? '#f0fdf4' : '#fef2f2', color: yearSaveMsg.startsWith('✅') ? '#16a34a' : '#dc2626' }}>{yearSaveMsg}</div>}

          <div style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280', lineHeight: 1.6 }}>
            <div>📌 Previous: <b>2025-2026</b></div>
            <div>✅ Current: <b>{currentAcademicYear}</b></div>
            <div style={{ marginTop: '4px', color: '#9ca3af' }}>Old marksheets/results are never changed.</div>
          </div>
        </div>

        {/* Student ID Card Print */}
        <div className="card" style={{ padding: window.innerWidth <= 768 ? '15px' : '20px', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '2px solid #1e40af' }}>
          <h3 style={{ fontSize: window.innerWidth <= 768 ? '18px' : '20px', marginBottom: '12px', color: '#1e40af' }}>🆔 Student ID Cards</h3>
          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
            Download ID cards for an entire class or selected students. Supports bulk and selective download with duplex printing.
          </p>
          <button
            onClick={() => setShowBulkIdCard(true)}
            style={{
              width: '100%', padding: '12px',
              background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '15px', fontWeight: 'bold', cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(30, 64, 175, 0.3)'
            }}
          >
            🖨️ Print Student ID Cards
          </button>
        </div>

        {/* Teacher ID Card Print */}
        <div className="card" style={{ padding: window.innerWidth <= 768 ? '15px' : '20px', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '2px solid #1e40af' }}>
          <h3 style={{ fontSize: window.innerWidth <= 768 ? '18px' : '20px', marginBottom: '12px', color: '#1e40af' }}>👨🏫 Teacher ID Cards</h3>
          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
            Download ID cards for all staff or selected teachers. Includes designation, class, subject, and contact info.
          </p>
          <button
            onClick={() => setShowTeacherIdCard(true)}
            style={{
              width: '100%', padding: '12px',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '15px', fontWeight: 'bold', cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(30, 58, 138, 0.3)'
            }}
          >
            🖨️ Print Teacher ID Cards
          </button>
        </div>

        {/* Admit Cards (10-on-1 A4 Monochrome) */}
        <div className="card" style={{ padding: window.innerWidth <= 768 ? '15px' : '20px', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', border: '2px solid #000000' }}>
          <h3 style={{ fontSize: window.innerWidth <= 768 ? '18px' : '20px', marginBottom: '8px', color: '#000000' }}>🎟️ Admit Card Generator</h3>
          <p style={{ fontSize: '13px', color: '#475569', marginBottom: '14px' }}>
            Batch print 10 compact admit cards per A4 portrait page in razor-sharp monochrome (pure black &amp; white) with dashed cut lines.
          </p>
          <div style={{ fontSize: '12px', color: '#1e293b', marginBottom: '14px', lineHeight: 1.7 }}>
            <div>📐 2×5 Grid · 95mm × 52mm Cards · Dashed Crop Boundaries</div>
            <div>📋 Filter by Class/Section · Auto-sort Roll/Name · High-Speed PDF</div>
          </div>
          <button
            onClick={() => setShowAdmitCard(true)}
            style={{
              width: '100%', padding: '12px',
              background: '#000000',
              color: '#ffffff', border: 'none', borderRadius: '10px',
              fontSize: '15px', fontWeight: 'bold', cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)'
            }}
          >
            🎟️ Open Admit Card Generator
          </button>
        </div>

        {/* Teacher Documents */}
        <div className="card" style={{ padding: window.innerWidth <= 768 ? '15px' : '20px', background: 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)', border: '2px solid #b45309' }}>
          <h3 style={{ fontSize: window.innerWidth <= 768 ? '18px' : '20px', marginBottom: '8px', color: '#92400e' }}>📄 Teacher Documents</h3>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '14px' }}>
            Generate official Joining Letters and Experience Certificates for teachers with auto-populated data, unique reference numbers, A4 preview, and PDF download.
          </p>
          <div style={{ fontSize: '12px', color: '#78350f', marginBottom: '14px', lineHeight: 1.7 }}>
            <div>📝 Joining Letter &nbsp;·&nbsp; 🏅 Experience Letter</div>
            <div>📋 Issued Letters History &nbsp;·&nbsp; 🔄 Revise Documents</div>
          </div>
          <button
            onClick={() => setShowTeacherDocs(true)}
            style={{
              width: '100%', padding: '12px',
              background: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '15px', fontWeight: 'bold', cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(146,64,14,0.3)'
            }}
          >
            📄 Open Teacher Documents
          </button>
        </div>

        {/* AI Question Paper Generator (principal / admin only) */}
        {['principal', 'admin'].includes(user?.role) && (
          <div className="card" style={{ padding: window.innerWidth <= 768 ? '15px' : '20px', background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: '2px solid #4f46e5' }}>
            <h3 style={{ fontSize: window.innerWidth <= 768 ? '18px' : '20px', marginBottom: '8px', color: '#3730a3' }}>📄 AI Paper Generator</h3>
            <p style={{ fontSize: '13px', color: '#475569', marginBottom: '14px' }}>
              Upload an NCERT / textbook chapter PDF and generate a source-grounded exam paper in English, Hindi or Bilingual with an editable preview and answer key.
            </p>
            <div style={{ fontSize: '12px', color: '#312e81', marginBottom: '14px', lineHeight: 1.7 }}>
              <div>📚 PDF-only content · 🧮 Auto marks check · 🔄 Regenerate any question</div>
              <div>🖨️ A4 Question Paper PDF · 🔒 Teacher Answer Key PDF</div>
            </div>
            <button
              onClick={() => setShowAIPaper(true)}
              style={{
                width: '100%', padding: '12px',
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                color: 'white', border: 'none', borderRadius: '10px',
                fontSize: '15px', fontWeight: 'bold', cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(79, 70, 229, 0.3)'
              }}
            >
              📄 Open AI Paper Generator
            </button>
            <button
              onClick={() => setShowPaperMonitor(true)}
              style={{
                width: '100%', padding: '12px', marginTop: '10px',
                background: '#eef2ff', color: '#3730a3', border: '2px solid #c7d2fe', borderRadius: '10px',
                fontSize: '15px', fontWeight: 'bold', cursor: 'pointer'
              }}
            >
              📈 Teacher Paper Activity &amp; Files
            </button>
          </div>
        )}
        {['principal', 'admin'].includes(user?.role) && (
          <div className="card" style={{ padding: window.innerWidth <= 768 ? '15px' : '20px', background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '2px solid #059669' }}>
            <h3 style={{ fontSize: window.innerWidth <= 768 ? '18px' : '20px', marginBottom: '8px', color: '#065f46' }}>📑 Examination &amp; Paper Control Center</h3>
            <p style={{ fontSize: '13px', color: '#475569', marginBottom: '14px' }}>
              Build AI + manual question papers for any class, review and approve teacher papers, and set the school-wide master header.
            </p>
            <button
              onClick={() => setShowPaperControl(true)}
              style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              📑 Open Paper Control Center
            </button>
          </div>
        )}
        <div id="school-analytics-hub" style={{ gridColumn: '1 / -1' }}>
          <button
            onClick={() => setShowSchoolHub((v) => !v)}
            style={{
              width: '100%', padding: '14px',
              background: showSchoolHub ? '#0f172a' : 'linear-gradient(135deg, #0891b2 0%, #4f46e5 100%)',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '16px', fontWeight: 'bold', cursor: 'pointer'
            }}
          >
            {showSchoolHub ? '✖ Close School Analytics Hub' : '📊 School Analytics Hub'}
          </button>
          {showSchoolHub && (
            <div style={{ marginTop: '12px' }}>
              <ExecutiveSchoolHub user={user} />
            </div>
          )}
        </div>
        <AttendanceLauncher user={user} />
        <div id="bulk-data-manager" style={{ gridColumn: '1 / -1' }}>
          <BulkDataManager user={user} />
        </div>
        <div id="diary-manager" style={{ gridColumn: '1 / -1' }}>
          <DiaryManagerPanel user={user} />
        </div>
        {['principal', 'admin'].includes(user?.role) && (
          <div style={{ gridColumn: '1 / -1' }}>
            <PrincipalComplaintsPanel user={user} />
          </div>
        )}
        {recordTeacher && (
          <TeacherRecordView
            teacher={recordTeacher}
            user={user}
            attendanceStatus={getTeacherAttendanceStatus(recordTeacher._id)}
            onClose={() => setRecordTeacher(null)}
          />
        )}
        <div style={{ gridColumn: '1 / -1' }}>
          <OnlineStaffTracker user={user} />
        </div>
        {['principal', 'admin'].includes(user?.role) && (
          <div id="admin-stream-control" style={{ gridColumn: '1 / -1' }}>
            <AdminStreamControl />
          </div>
        )}
        <div className="card" style={{ padding: '20px', background: '#f0fdf4', border: '2px solid #25d366' }}>
          <h3 style={{ marginBottom: '12px', color: '#15803d' }}>WhatsApp Class Group Links</h3>
          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
            Paste, update or remove the group invite link for every class (Nursery to 12th). Used for daily attendance reports.
          </p>
          <button
            onClick={() => setShowWaSettings(true)}
            style={{ width: '100%', padding: '12px', background: '#25d366', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}
          >
            Manage WhatsApp Links
          </button>
        </div>
        <div className="card" style={{ padding: window.innerWidth <= 768 ? '15px' : '20px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '2px solid #16a34a' }}>
          <h3 style={{ fontSize: window.innerWidth <= 768 ? '18px' : '20px', marginBottom: '12px', color: '#15803d' }}>🎓 Promote Student</h3>
          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
            Move a student to the next class and create their academic history record.
          </p>
          <button
            onClick={() => setShowPromoteModal(true)}
            style={{ width:'100%', padding:'12px', background:'linear-gradient(135deg,#16a34a,#22c55e)', color:'#fff', border:'none', borderRadius:'10px', fontSize:'15px', fontWeight:'bold', cursor:'pointer', boxShadow:'0 4px 15px rgba(22,163,74,0.3)' }}
          >
            Promote Student
          </button>
        </div>

        {/* Run Migration Card */}
        <div className="card" style={{ padding: window.innerWidth <= 768 ? '15px' : '20px', background: 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)', border: '2px solid #ca8a04' }}>
          <h3 style={{ fontSize: window.innerWidth <= 768 ? '18px' : '20px', marginBottom: '12px', color: '#92400e' }}>🔄 Academic History Migration</h3>
          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
            Backfill academic history for students who were already promoted before this feature was added.
          </p>
          <button
            onClick={() => setShowMigrationModal(true)}
            style={{ width:'100%', padding:'12px', background:'linear-gradient(135deg,#d97706,#f59e0b)', color:'#fff', border:'none', borderRadius:'10px', fontSize:'15px', fontWeight:'bold', cursor:'pointer', boxShadow:'0 4px 15px rgba(217,119,6,0.3)' }}
          >
            Run Migration
          </button>
        </div>
        <div className="pd-section-title"><h2>🏫 School Operations</h2><span>Staff attendance, student data, reports and administration</span></div>
        <div className="card" style={{ padding: window.innerWidth <= 768 ? '15px' : '20px' }}>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>Principal Dashboard</strong></p>
            <div style={{ fontSize: '14px' }}>
              <p>✅ Oversee school operations</p>
              <p>✅ Monitor all departments</p>
              <p>✅ Academic supervision</p>
              <p>✅ Staff coordination</p>
              <p>✅ Student affairs</p>
            </div>
          </div>
          <button className="btn" onClick={() => alert('School operations panel opened!')}>School Operations</button>
          <button className="btn" onClick={() => alert('Academic supervision opened!')}>Academic Supervision</button>
        </div>

        <div className="card" style={{ 
          gridColumn: window.innerWidth <= 768 ? 'span 1' : 'span 2',
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>👨🏫 Teachers & Tuition Teachers Overview</h3>
          <div style={{ marginBottom: '15px', padding: '10px', background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>{getPresentTeachersCount()}/{users.filter(u => u.role === 'teacher' || u.role === 'tuition_teacher').length}</div>
            <div style={{ fontSize: '14px', color: '#2e7d32' }}>Teachers Present Today</div>
          </div>
          <div>
            {users.filter(u => u.role === 'teacher' || u.role === 'tuition_teacher').map(teacher => {
              const status = getTeacherAttendanceStatus(teacher._id);
              return (
                <div key={teacher._id} style={{ 
                  display: 'flex', 
                  flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                  justifyContent: 'space-between', 
                  alignItems: window.innerWidth <= 768 ? 'stretch' : 'center', 
                  marginBottom: '12px', 
                  padding: window.innerWidth <= 768 ? '12px' : '8px', 
                  background: status === 'present' ? '#d4edda' : status === 'absent' ? '#f8d7da' : status === 'late' ? '#fff3cd' : '#f8f9fa', 
                  borderRadius: '8px', 
                  border: '1px solid #ddd',
                  gap: window.innerWidth <= 768 ? '10px' : '8px'
                }}>
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: window.innerWidth <= 768 ? '16px' : '14px' }}>{teacher.name}</strong>
                    <p style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '13px' : '11px', color: '#666' }}>{teacher.role === 'tuition_teacher' ? 'Tuition Teacher' : (teacher.class || 'Unassigned')}</p>
                    <p style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '12px' : '10px', color: '#888' }}>{teacher.email}</p>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: window.innerWidth <= 768 ? '6px' : '8px',
                    flexWrap: 'wrap',
                    justifyContent: window.innerWidth <= 768 ? 'space-between' : 'flex-end'
                  }}>
                    <span style={{ 
                      fontSize: window.innerWidth <= 768 ? '12px' : '10px', 
                      padding: window.innerWidth <= 768 ? '4px 8px' : '2px 6px', 
                      borderRadius: '3px', 
                      background: status === 'present' ? '#28a745' : status === 'absent' ? '#dc3545' : status === 'late' ? '#ffc107' : '#6c757d', 
                      color: 'white', 
                      fontWeight: 'bold' 
                    }}>
                      {status ? status.toUpperCase() : 'N/A'}
                    </span>
                    <button 
                      onClick={() => teacher.role === 'tuition_teacher' ? impersonateTuitionTeacher(teacher) : impersonateTeacher(teacher)}
                      style={{ 
                        fontSize: window.innerWidth <= 768 ? '12px' : '10px', 
                        padding: window.innerWidth <= 768 ? '8px 12px' : '6px 10px', 
                        background: teacher.role === 'tuition_teacher' ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' : 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer', 
                        fontWeight: 'bold',
                        minHeight: window.innerWidth <= 768 ? '36px' : '32px',
                        boxShadow: teacher.role === 'tuition_teacher' ? '0 3px 10px rgba(40, 167, 69, 0.3)' : '0 3px 10px rgba(0, 123, 255, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (teacher.role === 'tuition_teacher') {
                          e.target.style.background = 'linear-gradient(135deg, #20c997 0%, #17a2b8 100%)';
                        } else {
                          e.target.style.background = 'linear-gradient(135deg, #0056b3 0%, #004085 100%)';
                        }
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        if (teacher.role === 'tuition_teacher') {
                          e.target.style.background = 'linear-gradient(135deg, #28a745 0%, #20c997 100%)';
                        } else {
                          e.target.style.background = 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)';
                        }
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      🚪 Access Account
                    </button>
                    <button 
                      onClick={() => viewTeacherProfile(teacher)}
                      style={{ 
                        fontSize: window.innerWidth <= 768 ? '12px' : '10px', 
                        padding: window.innerWidth <= 768 ? '8px 12px' : '4px 8px', 
                        background: '#007bff', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer', 
                        fontWeight: 'bold',
                        minHeight: window.innerWidth <= 768 ? '36px' : 'auto'
                      }}
                    >
                      🔍 Profile
                    </button>
                    <button 
                      onClick={() => viewTeacherDashboard(teacher)}
                      style={{ 
                        fontSize: window.innerWidth <= 768 ? '12px' : '10px', 
                        padding: window.innerWidth <= 768 ? '8px 12px' : '4px 8px', 
                        background: '#28a745', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer', 
                        fontWeight: 'bold',
                        minHeight: window.innerWidth <= 768 ? '36px' : 'auto'
                      }}
                    >
                      📊 Dashboard
                    </button>
                    <button 
                      onClick={() => rankTeacher(teacher)}
                      style={{ 
                        fontSize: window.innerWidth <= 768 ? '12px' : '10px', 
                        padding: window.innerWidth <= 768 ? '8px 12px' : '6px 10px', 
                        background: '#f59e0b', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer', 
                        fontWeight: 'bold', 
                        minWidth: window.innerWidth <= 768 ? '80px' : '50px', 
                        minHeight: window.innerWidth <= 768 ? '36px' : '30px'
                      }}
                    >
                      ⭐ Rank
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>📊 School Overview</h3>
          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
              <div style={{ padding: '8px', background: '#d4edda', borderRadius: '4px' }}>
                <strong>Students:</strong> {users.filter(u => u.role === 'student').length}
              </div>
              <div style={{ padding: '8px', background: '#cce5ff', borderRadius: '4px' }}>
                <strong>Teachers:</strong> {users.filter(u => u.role === 'teacher').length}
              </div>
              <div style={{ padding: '8px', background: '#fff3cd', borderRadius: '4px' }}>
                <strong>Admin L1:</strong> {users.filter(u => u.role === 'admin1').length}
              </div>
              <div style={{ padding: '8px', background: '#f8d7da', borderRadius: '4px' }}>
                <strong>Principals:</strong> {users.filter(u => u.role === 'principal').length}
              </div>
            </div>
          </div>
          <button className="btn" onClick={() => alert('Detailed analytics opened!')}>View Analytics</button>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>📊 Academic Reports</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>Performance Analytics:</strong></p>
            <div style={{ fontSize: '14px' }}>
              <p>Student Performance: 85% Average</p>
              <p>Teacher Efficiency: 92%</p>
              <p>Attendance Rate: 88%</p>
              <p>Academic Progress: On Track</p>
            </div>
          </div>
          <button className="btn" onClick={() => alert('Detailed academic reports opened!')}>View Reports</button>
          <button className="btn" onClick={() => alert('Performance analytics opened!')}>Analytics</button>
        </div>

        <div className="card" style={{ 
          gridColumn: window.innerWidth <= 768 ? 'span 1' : 'span 2',
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>👨🏫 Teacher Attendance Management</h3>
          <div style={{ marginBottom: '15px', padding: '10px', background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', borderRadius: '8px', border: '2px solid #4caf50' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ margin: 0, color: '#2e7d32' }}>📅 Teacher Attendance - {selectedDate}</h4>
              <input 
                type="date" 
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{ padding: '6px', border: '2px solid #4caf50', borderRadius: '6px', fontSize: '12px' }}
              />
            </div>
            <div style={{ marginBottom: '15px', padding: '8px', background: '#f0f8ff', borderRadius: '6px', display: 'flex', justifyContent: 'space-around', fontSize: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', color: '#4caf50' }}>{Array.isArray(teacherAttendanceData) ? teacherAttendanceData.filter(att => att.status === 'present' && att.date === selectedDate).length : 0}</div>
                <div>Present</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', color: '#f44336' }}>{Array.isArray(teacherAttendanceData) ? teacherAttendanceData.filter(att => att.status === 'absent' && att.date === selectedDate).length : 0}</div>
                <div>Absent</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', color: '#ff9800' }}>{Array.isArray(teacherAttendanceData) ? teacherAttendanceData.filter(att => att.status === 'late' && att.date === selectedDate).length : 0}</div>
                <div>Late</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', color: '#2196f3' }}>{users.filter(u => u.role === 'teacher').length}</div>
                <div>Total</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
              {users.filter(u => u.role === 'teacher').map(teacher => {
                const currentStatus = getTeacherAttendanceStatus(teacher._id);
                return (
                  <div key={teacher._id} style={{ 
                    padding: '10px', 
                    background: 'white', 
                    borderRadius: '8px', 
                    border: currentStatus ? '2px solid #4caf50' : '1px solid #ddd'
                  }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '5px' }}>{teacher.name}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>{teacher.email}</div>
                    <div style={{ fontSize: '12px', marginBottom: '8px' }}>Class: {teacher.class || 'Unassigned'}</div>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      <button 
                        onClick={() => editTeacherAttendance(teacher._id, teacher.name, 'present')}
                        style={{ 
                          background: currentStatus === 'present' ? '#4caf50' : '#e0e0e0',
                          color: currentStatus === 'present' ? 'white' : '#666',
                          border: 'none', 
                          padding: '4px 8px', 
                          borderRadius: '4px', 
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}
                      >
                        ✓ Present
                      </button>
                      <button 
                        onClick={() => editTeacherAttendance(teacher._id, teacher.name, 'absent')}
                        style={{ 
                          background: currentStatus === 'absent' ? '#f44336' : '#e0e0e0',
                          color: currentStatus === 'absent' ? 'white' : '#666',
                          border: 'none', 
                          padding: '4px 8px', 
                          borderRadius: '4px', 
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}
                      >
                        ✗ Absent
                      </button>
                      <button 
                        onClick={() => editTeacherAttendance(teacher._id, teacher.name, 'late')}
                        style={{ 
                          background: currentStatus === 'late' ? '#ff9800' : '#e0e0e0',
                          color: currentStatus === 'late' ? 'white' : '#666',
                          border: 'none', 
                          padding: '4px 8px', 
                          borderRadius: '4px', 
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}
                      >
                        ⏰ Late
                      </button>
                    </div>
                    {currentStatus && (
                      <div style={{ marginTop: '5px', fontSize: '10px', color: '#666' }}>
                        Status: <span style={{ fontWeight: 'bold', color: currentStatus === 'present' ? '#4caf50' : currentStatus === 'absent' ? '#f44336' : '#ff9800' }}>{currentStatus.toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="card" style={{
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>📋 School Administration</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>Administrative Functions:</strong></p>
            <div style={{ fontSize: '14px' }}>
              <p>✅ Create/Manage Principals</p>
              <p>✅ Oversee Admin Level 1</p>
              <p>✅ School-wide Announcements</p>
              <p>✅ Academic Calendar</p>
              <p>✅ Performance Reports</p>
            </div>
          </div>
          <button className="btn" onClick={() => alert('School settings opened!')}>School Settings</button>
          <button className="btn" onClick={() => alert('Academic calendar opened!')}>Academic Calendar</button>
        </div>

        <div className="card" style={{ 
          gridColumn: window.innerWidth <= 768 ? 'span 1' : 'span 2',
          padding: window.innerWidth <= 768 ? '15px' : '20px'
        }}>
          <h3 style={{
            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
            marginBottom: window.innerWidth <= 768 ? '12px' : '15px'
          }}>👨🎓 Student Data Management</h3>
          <div style={{ marginBottom: '15px', padding: '10px', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1565c0' }}>{users.filter(u => u.role === 'student').length}</div>
            <div style={{ fontSize: '14px', color: '#1565c0' }}>Total Students</div>
          </div>
          <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <label style={{ fontWeight: 'bold' }}>Filter by Class:</label>
            <select 
              value={selectedClass} 
              onChange={(e) => setSelectedClass(e.target.value)}
              style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All Classes</option>
              {Object.keys(studentsByClass).sort().map(className => (
                <option key={className} value={className}>{className}</option>
              ))}
            </select>
            <button 
              className="btn" 
              onClick={() => setShowStudentData(!showStudentData)}
              style={{ background: showStudentData ? '#dc3545' : '#007bff' }}
            >
              {showStudentData ? '🔼 Hide Data' : '🔽 View Complete Data'}
            </button>
          </div>
          
          {showStudentData && (
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '8px', padding: '10px' }}>
              {selectedClass === 'all' ? (
                Object.keys(studentsByClass).sort().map(className => (
                  <div key={className} style={{ marginBottom: '20px' }}>
                    <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>Class {className} ({studentsByClass[className].length} students)</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
                      {studentsByClass[className].map(student => (
                        <div key={student._id} style={{ padding: '10px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: student.profilePicture ? `url(${student.profilePicture}) center/cover` : '#007bff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '16px' }}>
                              {!student.profilePicture && '👤'}
                            </div>
                            <div>
                              <strong style={{ fontSize: '14px' }}>{student.name}</strong>
                              <div style={{ fontSize: '12px', color: '#666' }}>ID: {student.studentId || student._id?.slice(-6)}</div>
                            </div>
                          </div>
                          <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                            <p><strong>Overall Grade:</strong> {student.overallGrade || 'B+'}</p>
                            <p><strong>Attendance:</strong> {student.presentDays || '165'}/{student.totalDays || '180'} ({student.presentDays && student.totalDays ? Math.round((student.presentDays/student.totalDays)*100) : '92'}%)</p>
                            <p><strong>Behavior:</strong> {student.behaviorScore || '95'}/100</p>
                            <p><strong>Guardian:</strong> {student.guardianPhone || 'N/A'}</p>
                            <div style={{ marginTop: '8px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              <button 
                                onClick={() => viewClassMarksheets(className)}
                                style={{ background: '#ffc107', color: '#212529', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', fontWeight: '600' }}
                              >
                                📋 Marksheets
                              </button>
                              <button 
                                onClick={() => viewClassAttendance(className)}
                                style={{ background: '#17a2b8', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', fontWeight: '600' }}
                              >
                                📅 Attendance
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                studentsByClass[selectedClass] && (
                  <div>
                    <h4 style={{ color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>Class {selectedClass} ({studentsByClass[selectedClass].length} students)</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
                      {studentsByClass[selectedClass].map(student => (
                        <div key={student._id} style={{ padding: '10px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: student.profilePicture ? `url(${student.profilePicture}) center/cover` : '#007bff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '16px' }}>
                              {!student.profilePicture && '👤'}
                            </div>
                            <div>
                              <strong style={{ fontSize: '14px' }}>{student.name}</strong>
                              <div style={{ fontSize: '12px', color: '#666' }}>ID: {student.studentId || student._id?.slice(-6)}</div>
                            </div>
                          </div>
                          <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                            <p><strong>Email:</strong> {student.email}</p>
                            <p><strong>Father's Name:</strong> {student.fathersName || 'N/A'}</p>
                            <p><strong>Section:</strong> {student.section || 'N/A'}</p>
                            <p><strong>Overall Grade:</strong> {student.overallGrade || 'N/A'}</p>
                            <p><strong>Math Grade:</strong> {student.mathGrade || 'N/A'}</p>
                            <p><strong>Science Grade:</strong> {student.scienceGrade || 'N/A'}</p>
                            <p><strong>English Grade:</strong> {student.englishGrade || 'N/A'}</p>
                            <p><strong>Attendance:</strong> {student.presentDays || 'N/A'}/{student.totalDays || 'N/A'} ({student.presentDays && student.totalDays ? Math.round((student.presentDays/student.totalDays)*100) : 'N/A'}%)</p>
                            <p><strong>Behavior Score:</strong> {student.behaviorScore || 'N/A'}</p>
                            <p><strong>Warnings:</strong> {student.warnings || '0'}</p>
                            <p><strong>Commendations:</strong> {student.commendations || '0'}</p>
                            <p><strong>Assignments Completed:</strong> {student.completedAssignments || 'N/A'}</p>
                            <p><strong>Guardian Name:</strong> {student.guardianName || 'N/A'}</p>
                            <p><strong>Guardian Phone:</strong> {student.guardianPhone || 'N/A'}</p>
                            <p><strong>Guardian Email:</strong> {student.guardianEmail || 'N/A'}</p>
                            <p><strong>Last Contact:</strong> {student.lastContact || 'N/A'}</p>
                            <p><strong>Account Created:</strong> {student.createdAt ? new Date(student.createdAt).toLocaleDateString() : 'N/A'}</p>
                            <div style={{ marginTop: '8px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              <button 
                                onClick={() => viewClassMarksheets(selectedClass)}
                                style={{ background: '#ffc107', color: '#212529', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', fontWeight: '600' }}
                              >
                                📋 Marksheets
                              </button>
                              <button 
                                onClick={() => viewClassAttendance(selectedClass)}
                                style={{ background: '#17a2b8', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', fontWeight: '600' }}
                              >
                                📅 Attendance
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {showBulkIdCard && (
        <BulkIdCardPrint
          user={user}
          onClose={() => setShowBulkIdCard(false)}
          academicYear={currentAcademicYear}
        />
      )}

      {showTeacherIdCard && (
        <BulkTeacherIdCardPrint
          user={user}
          onClose={() => setShowTeacherIdCard(false)}
          academicYear={currentAcademicYear}
        />
      )}

      {showAdmitCard && (
        <BulkAdmitCardPrint
          user={user}
          onClose={() => setShowAdmitCard(false)}
        />
      )}

      {showTeacherDocs && (
        <TeacherDocuments
          user={user}
          onClose={() => setShowTeacherDocs(false)}
        />
      )}

      {showPaperControl && <QuestionPaperBuilder user={user} isPrincipal onClose={() => setShowPaperControl(false)} />}

      {showPaperMonitor && <PrincipalPaperMonitor user={user} onClose={() => setShowPaperMonitor(false)} />}

      {showWaSettings && <PrincipalWhatsAppSettings user={user} onClose={() => setShowWaSettings(false)} />}

      {showAIPaper && (
        <AIPaperGenerator
          user={user}
          onClose={() => setShowAIPaper(false)}
          academicYear={currentAcademicYear}
        />
      )}

      {/* Promote Student Modal */}
      {showPromoteModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:'100%', maxWidth:480, boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
            <h3 style={{ color:'#1e40af', marginBottom:18 }}>Promote Student</h3>

            <label style={{ fontSize:13, fontWeight:600, color:'#374151' }}>Select Student</label>
            <select value={promoteForm.studentId} onChange={e => {
              const s = allStudents.find(x => x._id === e.target.value);
              setPromoteForm(f => ({ ...f, studentId: e.target.value, fromClass: s?.class || '' }));
            }} style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1.5px solid #1e40af', marginBottom:12, fontSize:14 }}>
              <option value=''>-- Select Student --</option>
              {allStudents.map(s => <option key={s._id} value={s._id}>{s.name} (Class {s.class})</option>)}
            </select>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#374151' }}>From Class</label>
                <input value={promoteForm.fromClass} readOnly style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1.5px solid #d1d5db', background:'#f3f4f6', fontSize:14 }} />
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#374151' }}>To Class</label>
                <select value={promoteForm.toClass} onChange={e => setPromoteForm(f => ({ ...f, toClass: e.target.value }))} style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1.5px solid #1e40af', fontSize:14 }}>
                  <option value=''>-- Select --</option>
                  {['Nursery','LKG','UKG','HKG','1','2','3','4','5','6','7','8','9','10'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#374151' }}>From Year</label>
                <input value={promoteForm.fromYear} onChange={e => setPromoteForm(f => ({ ...f, fromYear: e.target.value }))} style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1.5px solid #1e40af', fontSize:14 }} placeholder='2024-25' />
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#374151' }}>To Year</label>
                <input value={promoteForm.toYear} onChange={e => setPromoteForm(f => ({ ...f, toYear: e.target.value }))} style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:'1.5px solid #1e40af', fontSize:14 }} placeholder='2025-26' />
              </div>
            </div>

            {promoteMsg && <div style={{ padding:'8px 12px', borderRadius:8, background: promoteMsg.startsWith('Error') ? '#fee2e2' : '#dcfce7', color: promoteMsg.startsWith('Error') ? '#dc2626' : '#16a34a', marginBottom:12, fontSize:13 }}>{promoteMsg}</div>}

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => { setShowPromoteModal(false); setPromoteMsg(''); }} style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid #d1d5db', background:'#f3f4f6', cursor:'pointer', fontWeight:600 }}>Cancel</button>
              <button onClick={handlePromoteStudent} style={{ padding:'9px 18px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#1e40af,#3b82f6)', color:'#fff', cursor:'pointer', fontWeight:700 }}>Promote</button>
            </div>
          </div>
        </div>
      )}

      {/* Migration Modal */}
      {showMigrationModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:'100%', maxWidth:520, boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
            <h3 style={{ color:'#1e40af', marginBottom:8 }}>Run Academic History Migration</h3>
            <p style={{ fontSize:13, color:'#6b7280', marginBottom:18 }}>This will backfill academic history for all already-promoted students. Safe to run multiple times — no data will be deleted or overwritten.</p>

            {!migrationResult && (
              <button onClick={handleRunMigration} style={{ width:'100%', padding:12, borderRadius:10, border:'none', background:'linear-gradient(135deg,#1e40af,#3b82f6)', color:'#fff', fontWeight:700, fontSize:15, cursor:'pointer', marginBottom:12 }}>
                Run Migration Now
              </button>
            )}

            {migrationResult && !migrationResult.error && (
              <div style={{ background:'#f0fdf4', border:'1.5px solid #86efac', borderRadius:10, padding:16, marginBottom:12 }}>
                <div style={{ fontWeight:700, color:'#16a34a', marginBottom:8 }}>Migration Complete</div>
                <div style={{ fontSize:13, color:'#374151', lineHeight:1.8 }}>
                  <div>Total students: <b>{migrationResult.total}</b></div>
                  <div>Migrated: <b>{migrationResult.migrated}</b></div>
                  <div>Skipped (no prev marksheet): <b>{migrationResult.skipped}</b></div>
                  <div>Failed: <b>{migrationResult.failed}</b></div>
                  <div>Academic year: <b>{migrationResult.currentYear}</b></div>
                </div>
              </div>
            )}

            {migrationResult?.error && (
              <div style={{ background:'#fef2f2', border:'1.5px solid #fca5a5', borderRadius:10, padding:16, marginBottom:12, color:'#dc2626' }}>
                Error: {migrationResult.error}
              </div>
            )}

            <div style={{ display:'flex', justifyContent:'flex-end' }}>
              <button onClick={() => { setShowMigrationModal(false); setMigrationResult(null); }} style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid #d1d5db', background:'#f3f4f6', cursor:'pointer', fontWeight:600 }}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
}

export default PrincipalDashboard;